'use client';

import { useEffect } from 'react';

import { usePathname } from 'next/navigation';

/**
 * Conditionally injects the marketing-site sales-agent chatbot.
 *
 * The widget script uses a hardcoded host id (`#leagen-chatbot`) internally
 * and only expects one *floating* instance per page. A few marketing routes
 * run their own floating agent for the page and would collide with the
 * general sales agent here — we skip injection only on those exact routes.
 *
 * Routes that DO get the sales agent:
 *   - All normal marketing pages (home, blog, pricing, faq, contact)
 *   - `/axcelerate` — the landing page. Aiman runs here as an inline iframe
 *     (separate document), so Sarah (sales agent) can safely coexist as the
 *     floating bottom-right widget.
 *
 * Routes that DON'T get the sales agent:
 *   - `/axcelerate/enrolment-confirmation` — Aiman is promoted to the
 *     floating script-mode widget on this page (post-payment continuity),
 *     so Sarah is suppressed to avoid a host-id collision.
 */

interface Props {
  agentId: string | undefined;
}

// Exact paths where the marketing layout should NOT inject the default
// sales agent because the page already renders its own floating chat.
const SKIP_EXACT_PATHS = new Set([
  '/axcelerate/enrolment-confirmation',
  '/design-standards', // Presentational component library, never a live agent surface.
]);

export function MarketingChatbotScript({ agentId }: Props) {
  const pathname = usePathname() ?? '';

  // bfcache reload guard.
  //
  // The chat widget mounts shadow-DOM under `#leagen-chatbot` in
  // document.body. When the user navigates to a page like
  // `/axcelerate/enrolment-confirmation` (where Aiman is the floating
  // widget), then uses the browser back/forward button to return to a
  // normal marketing page (where Sarah should be), the browser restores
  // the previous page from bfcache — DOM, JS state, and the Aiman widget
  // host are all brought back unchanged. No scripts re-run, so Sarah
  // never gets a chance to mount and the user sees Aiman stuck on what
  // should be a Sarah page.
  //
  // `pageshow` with `event.persisted === true` is the browser's signal
  // that the page was restored from bfcache. Force a fresh reload in
  // that case so the widget bundle re-evaluates cleanly.
  useEffect(() => {
    const handler = (e: PageTransitionEvent) => {
      if (e.persisted) {
        window.location.reload();
      }
    };
    window.addEventListener('pageshow', handler);
    return () => window.removeEventListener('pageshow', handler);
  }, []);

  if (!agentId) return null;
  if (SKIP_EXACT_PATHS.has(pathname)) return null;

  // `data-default-agent-id` is the page's lead agent — the agent that owns
  // the floating chat slot. On single-agent pages it mirrors `data-agent-id`.
  // On multi-agent pages (e.g. /axcelerate, where Sarah is the lead and
  // Aiman is reachable via a `<leagen-launcher agent-id={AIMAN_ID}>`) the
  // bundle's `readDefaultAgentId()` reads this attribute so closing
  // a launcher-driven panel auto-restores the LEAD agent (Sarah), not
  // whichever agent was last shown.
  //
  // See spec 024 §4 (page-side ergonomics) and Phase 14 plan 14-01
  // (`readDefaultAgentId()` implementation).
  return (
    <script
      src="/dist/leagen-chatbot.js"
      data-agent-id={agentId}
      data-default-agent-id={agentId}
      defer
    />
  );
}
